import { useEffect, useRef, useState } from 'react';

import { useOmniAds } from '@libs/utils/hooks/OmniAds';
import { useRedline } from '@libs/redline';
import useApiBillet from '@common/apis/billet/useApiBillet';

import constants from '@common/constants';

import { useBilletProps } from './types';
import { Billet } from '@dues/pages/Partner/ClosedAgreement/types';
import { pageNames } from '@common/routes/pageNames';
import { openBase64PdfInTab } from '@consumidor-positivo/ts-utils';
import * as caixa from '@dues/helpers/caixa';

const useBillet = ({
  agreement, // my agreements
  installment,
  msUrl,
  closedAgreement, // closed agreement
  isMyAgreementsPage = false,
  refCopiedWarning,
}: useBilletProps | any) => {
  const { track: trackRedline } = useRedline();
  const { omniTracker } = useOmniAds();

  const { apiGenerateBillet } = useApiBillet(msUrl);

  const initialBilletData = {
    linhaDigitavel: '',
    informacaoComplementar: '',
    pdf: '',
    hasBillet: false,
  };

  const [buttonText, setButtonText] = useState<string>('Copiar código do boleto');
  const [billet, setBillet] = useState<Billet>(closedAgreement?.billetData ? closedAgreement?.billetData : initialBilletData);
  const [hasBillet, setHasBillet] = useState<boolean>(closedAgreement?.billetData?.hasBillet);

  const [isBilletGenerationError, setIsBilletGenerationError] = useState<boolean>(false);

  const [loadingDownloadBillet, setLoadingDownloadBillet] = useState<boolean>(false);

  const [showSkeleton, setShowSkeleton] = useState<boolean>(false);

  const singlePixPaymentMethod = closedAgreement?.singlePaymentMethod && closedAgreement?.paymentMethod?.id === 'PIX';

  const isPollingBilletActive = useRef(singlePixPaymentMethod ? false : !closedAgreement?.billetData?.hasBillet);

  const [isPollingBillet, setIsPollingBillet] = useState<boolean>(isPollingBilletActive?.current);

  const getBilletCode = () => {
    const billetObject = installment?.properties?.find((prop: any) => prop.key === 'boleto');

    if (!billetObject) return billet.linhaDigitavel;

    return billetObject?.stringValue;
  };

  const handleCopyBilletCode = (codeToCopy: string) => {
    const redlineData = {
      paymentType: 'BILLET',
      code: codeToCopy,
      agreementId: agreement?.id || closedAgreement?.agreement?.id,
      outcomeType: 'COPY',
      installmentId: String(installment?.id),
    } as const;

    trackRedline.renegkit.paymentButtonClicked(redlineData);

    const eventName = 'agreements.PaymentCodeCopied.v1'; // TODO Renegkit Validate omni
    const payloadOmni = {
      code: codeToCopy,
      codeType: 'BILLET',
      agreementId: agreement?.id || closedAgreement?.agreement?.id,
      debtId: agreement?.debt?.id || closedAgreement?.debt?.id,
      installmentId: installment?.id,
      installment: installment?.installmentNumber || installment?.parcela,
      installmentValue: installment?.installmentValue || installment?.valorParcela,
      type: 'BILLET',
    };

    const msReturnDefaultButton = 2000;

    navigator.clipboard
      .writeText(codeToCopy)
      .then(() => {
        trackRedline.renegkit.paymentCodeRetrieved(redlineData);

        omniTracker.all({
          google: {
            target: 'gALL',
            conversionId: 'AW-392945561/6rcWCK2ZpoIbEJm_r7sB',
            eventName: eventName,
            payload: {
              ...payloadOmni,
            },
          },
          meta: {
            eventName: 'Custom',
            payload: {
              eventName: eventName,
              ...payloadOmni,
            },
          },
        });

        setButtonText('Código do boleto copiado');

        if (refCopiedWarning?.current) {
          refCopiedWarning.current.open({
            visible: true,
            typeCode: 'boleto',
          });
        }

        setTimeout(() => {
          setButtonText('Copiar código do boleto');

          if (refCopiedWarning?.current) {
            refCopiedWarning.current.open({
              visible: false,
            });
          }
        }, msReturnDefaultButton);
      })
      .catch((error) => {
        trackRedline.renegkit.paymentCodeRetrievalFailed({
          ...redlineData,
          error: error.message,
        });

        console.error('[COPY BILLET CODE]:', error);
      });
  };

  const handleBilletDownload = () => {
    const billetLink = billet.link || caixa.getCaixaExternalBillet(installment);
    const trackPayload = {
      code: (billetLink || billet.pdf) as string,
      outcomeType: 'DOWNLOAD',
      paymentType: 'BILLET',
      agreementId: agreement?.id || closedAgreement?.agreement?.id,
      installmentId: String(installment?.id),
    } as const;

    trackRedline.renegkit.paymentButtonClicked(trackPayload);

    if (billetLink || billet.pdf) {
      trackRedline.renegkit.paymentCodeRetrieved(trackPayload);
    }

    if (billetLink) {
      window.open(billetLink, '_blank');
    } else if (billet.pdf) {
      openBase64PdfInTab(billet.pdf);
    } else {
      const payload = {
        installmentCode: installment?.installmentCode,
        parameters: {
          partnerSystemIdentifier: agreement?.partner?.identificador,
          requestParameters: {
            dividaId: agreement?.debt?.id,
            opcaoPagamentoId: installment?.id,
          },
        },
      };

      setLoadingDownloadBillet(true);

      apiGenerateBillet
        .send(payload)
        .then((response) => {
          setLoadingDownloadBillet(false);
          trackRedline.renegkit.paymentCodeRetrieved(trackPayload);
          setBillet(response);
          setHasBillet(response?.hasBillet);
          setIsBilletGenerationError(false);
          openBase64PdfInTab(response.pdf);
        })
        .catch((err) => {
          trackRedline.renegkit.paymentCodeRetrievalFailed({
            ...trackPayload,
            error: String(err),
          });
          setHasBillet(false);
          setIsBilletGenerationError(true);
          setLoadingDownloadBillet(false);
          const billetDownloadError = {
            agreement,
            isMyAgreementsPage,
          };
          localStorage.setItem(constants.debts.BILLET_NOT_FOUND, JSON.stringify(billetDownloadError));
          window.open(pageNames.billetNotFoundError.path, '_blank');
        });
    }
  };

  const stopPollingGetBillet = () => {
    isPollingBilletActive.current = false;
    setIsPollingBillet(false);
  };

  const pollingGetBillet = (handleGenerateBillet: () => void) => {
    if (isMyAgreementsPage && getBilletCode()) {
      stopPollingGetBillet();
      return;
    }

    const timer = setTimeout(() => {
      stopPollingGetBillet();
    }, 10000);

    // if (ctrlFirstRequestBillet.current) {
    //   handleGenerateBillet();
    //   setTimeout(() => {
    //     ctrlFirstRequestBillet.current = false;
    //   }, 2050);
    // };

    const intervalBilletCode = setInterval(() => {
      //if (!ctrlFirstRequestBillet.current) handleGenerateBillet();
      handleGenerateBillet();

      if ((hasBillet && billet.linhaDigitavel) || !isPollingBilletActive.current) {
        stopPollingGetBillet();
        setShowSkeleton(true);

        clearTimeout(timer);
        clearInterval(intervalBilletCode);
      }
    }, 2000);

    return () => {
      clearInterval(intervalBilletCode);
      clearTimeout(timer);
    };
  };

  useEffect(() => {
    if (showSkeleton) {
      setTimeout(() => {
        setShowSkeleton(false);
      }, 2000);
    }
  }, [showSkeleton]);

  return {
    buttonText,
    handleCopyBilletCode,
    handleBilletDownload,

    billetCode: getBilletCode(),
    billetPDF: billet?.pdf,

    pollingGetBillet,
    isPollingBillet: !getBilletCode() ? isPollingBillet : false,

    hasBillet,
    setHasBillet,

    billet,
    setBillet,

    loadingDownloadBillet,

    showSkeleton,

    isBilletGenerationError,
    setIsBilletGenerationError
  };
};
export default useBillet;
